/**
 * @fileOverview The time-tracking walkthrough: both views of the feature, and — since Zeiten prüfen
 * absorbed four screens — the regions inside the second of them. Pure data.
 * See docs/view-help.md.
 */

import { FEATURE_SETS } from "@/services/featureSet"
import { PERMISSIONS } from "@/services/permission"
import { stepsOn } from "@/services/viewHelp/define"
import type { ViewHelpTour } from "@/services/viewHelp/types"

/** Reviewing other people's time is one permission, and every step outside Meine Zeiten needs it. */
const REVIEW_FOREIGN = [PERMISSIONS.VIEW_TIME_ENTRY_FOREIGN]

/** Recording for somebody else — the Sammelerfassung button only exists with it. */
const EDIT_FOREIGN = [PERMISSIONS.EDIT_TIME_ENTRY_FOREIGN]

const TIME_TRACKING = [FEATURE_SETS.TIME_TRACKING]

/** Reading the working-time models — the settings tab and the employee's assignment both need it. */
const VIEW_MODELS = [PERMISSIONS.VIEW_WORK_TIME_MODEL]

/** Opening a model for editing — the pencil only exists with it. */
const EDIT_MODELS = [PERMISSIONS.EDIT_WORK_TIME_MODEL]

/** Every step is add-on gated; the one view beyond Meine Zeiten is also permission gated. */
const onMy = stepsOn("TimeTrackingMy", { featureSets: TIME_TRACKING })
const onReview = stepsOn("TimeTrackingReview", { featureSets: TIME_TRACKING, permissions: REVIEW_FOREIGN })

// The related views: where the feature is set up — the settings' models tab, and a sample employee's
// company tab, where the model is assigned. The employee is the sandbox's Max Mustermann. The pointer
// walks there (`services/viewHelp/ways.ts`); `at` says where to load the frame if that walk ever fails.
const onModels = stepsOn("SettingsOverview", {
  featureSets: TIME_TRACKING,
  permissions: VIEW_MODELS,
  at: { hash: "worktimemodel" },
})
const onAssignment = stepsOn("EmployeeDetail", {
  featureSets: TIME_TRACKING,
  permissions: VIEW_MODELS,
  at: { params: { employeeId: "demo-1" } },
})

// And where the hours go afterwards: the project they were booked on, whose Zeiten register lists
// them and checks them against the project's reports — and which is where "was hat diese Baustelle an
// Stunden gekostet" is answered. The sample project is the world's Musterprojekt Nord, the site the
// clock suggests.
const onProject = stepsOn("ProjectDetail", {
  featureSets: TIME_TRACKING,
  at: { params: { projectId: "p-nord" }, hash: "timetracking" },
})
// Where the planning decides what these views expect: an absence planned there explains an empty
// day. The sample calendar is the planning tour's own.
const onPlanning = stepsOn("PersonnelCalendar", {
  featureSets: TIME_TRACKING,
  at: { params: { calendarId: "cal-fruehjahr" } },
})

/**
 * The time-tracking chain: record your own week, check the team's and see who is on the clock and who
 * is behind, read where the accounts stand, and find what broke the Arbeitszeitgesetz.
 *
 * Most steps look; the ones in a scene *do* something — open the entry form and fill it in, filter
 * the list by a day, tick a row to show the batch actions, switch between the review's four panels.
 * Each scene's `exit` takes away whatever would get in the way of the steps after it, which is what
 * keeps every step reachable from the timeline in any order. None of them ever presses a control that
 * saves: the frame is sealed and a save could not happen, but it would look like one that failed.
 *
 * Meine Zeiten carries no permission on purpose — the route does not either (see
 * `router/routes/timetracking.js`), because that page only ever shows the caller's own days.
 */
export const TIMETRACKING_TOUR: ViewHelpTour = {
  key: "timetracking",
  icon: "mdi-clock-outline",
  // In the order of the tab strip, so the walk from one view to the next is always to the tab beside it.
  routes: ["TimeTrackingMy", "TimeTrackingReview"],
  // Where it is set up and where its hours end up: the models every target is taken from, the
  // assignment that ties a person to one, the project the hours are booked on, and the planning whose
  // absences explain an empty day. Played last, after a rule on the timeline — other features' pages.
  related: ["SettingsOverview", "EmployeeDetail", "ProjectDetail", "PersonnelCalendar"],
  scenes: [
    // The clock's own project picker: pick another site, and leave it picked. Nothing later depends on
    // which site the clock shows, and putting the suggested one back meant typing into the same field
    // again while the next step came on — a second demonstration nobody asked for.
    { key: "clock-project", enter: [], exit: [] },
    // Clocked in, to show the clock where it follows the reader: the app bar. Safe to press in the frame:
    // the store keeps the clock there and sends nothing (`clockIn` in demo mode). Clocked out again
    // before anything else, since a running clock changes the panel the later steps point at.
    {
      key: "clock-running",
      enter: [{ do: "click", target: "tt-clock-in" }],
      exit: [{ do: "click", target: "tt-clock-out" }],
    },
    // Max's page: the pointer clicks its "Unternehmen" tab as a step of its own, then the page scrolls
    // down to the assignment. One scene, so a jump to the assignment clicks the tab first, at speed.
    // Nothing to undo: leaving the page takes the tab with it.
    { key: "employee-company", enter: [], exit: [] },
    // The project's Zeiten register, pressed by the first of its two steps; one scene, so a jump to the
    // reconciliation presses the tab first. Nothing to undo: leaving the page takes the choice with it.
    { key: "project-times", enter: [], exit: [] },
    // The planning board, brought onto today first: it opens at the earliest montage date of its
    // projects, months before anybody is planned, where the employee lane is empty.
    { key: "planning-board", enter: [{ do: "showToday", target: "planning-today" }], exit: [] },
    // A model opened from the settings list, and cancelled at the end — never saved.
    {
      key: "model-edit",
      enter: [{ do: "click", target: "tt-model-edit-first" }],
      exit: [{ do: "click", target: "form-dialog-cancel" }],
    },
    // The manual entry form, opened from the header and cancelled at the end — never saved.
    {
      key: "new-entry",
      enter: [{ do: "click", target: "tt-my-new" }],
      exit: [{ do: "click", target: "form-dialog-cancel" }],
    },
    // Opening the export menu shows what is in it; picking an item would download a real file.
    { key: "my-export", enter: [{ do: "click", target: "tt-my-export" }], exit: [{ do: "escape" }] },
    // A week back, then "Diese Woche" — robust whichever week the page happened to be on.
    {
      key: "week-paging",
      enter: [{ do: "click", target: "tt-my-week-prev" }],
      exit: [{ do: "click", target: "tt-my-week-today" }],
    },
    // Wednesday: a weekday with entries in the sample week. The chart ignores clicks on empty days.
    // From the top of the page, where chart and list sit side by side: scrolled down, the list the
    // click narrows starts above the fold, and the click seems to do nothing.
    {
      key: "day-filter",
      enter: [
        { do: "scroll", target: "tt-my-chart", to: "top" },
        { do: "check", target: "tt-chart-day-2" },
      ],
      exit: [{ do: "uncheck", target: "tt-chart-day-2" }],
    },
    // The whole project card brought on screen first, not just its first row: the reader should see
    // which of the rows is being clicked.
    {
      key: "project-filter",
      enter: [
        { do: "scroll", target: "tt-my-projects", to: "reveal" },
        { do: "check", target: "tt-project-first" },
      ],
      exit: [{ do: "uncheck", target: "tt-project-first" }],
    },
    // The people are picked by the step itself — see `review-filters` — and stay picked: the grid after
    // it is theirs, and ticking the others back in would be a second demonstration. Nothing else is
    // set, so there is nothing to undo.
    { key: "review-filters", enter: [], exit: [] },
    // The pointer resting on an empty day, and on a day's heading: both are offers that only show under
    // the mouse (`hover`). Nothing to undo — the pointer leaving takes the look with it.
    { key: "create-hover", enter: [], exit: [] },
    { key: "day-head-hover", enter: [], exit: [] },
    // The day form opened from the empty day the step before rested on, so it arrives already set to
    // that person and that day — and saved at the end of the chapter, which the sandbox lets the demo
    // keep (`recordDemoCrew`), so the gap is then shown filled. The exit's Abbrechen finds nothing to
    // press after the save; it is there for a reader who leaves the chapter before it. So it is a
    // `cancel`, not a `click`: a click waited the whole action timeout for the gone button, and the
    // step after the save came up seconds late.
    {
      key: "crew",
      enter: [{ do: "click", target: "tt-matrix-create-first" }],
      exit: [{ do: "cancel", target: "form-dialog-cancel" }],
    },
    // The same form on its Abwesenheit tab, opened from the header's own button so the walk shows the
    // way in as well, and cancelled at the end — never saved. One scene for all three of its steps,
    // so the kind picked in the second is still there for the third.
    {
      key: "absence",
      enter: [{ do: "click", target: "tt-review-absence" }],
      exit: [{ do: "click", target: "form-dialog-cancel" }],
    },
    // The Anwesenheit banner, unfolded by its first step and folded again afterwards: it is closed
    // by default, and a walk that left it open would push everything under it down for every step
    // that follows. `expand`/`collapse` and not two clicks — a toggle pressed as an exit undoes
    // itself when the reader overtakes the step that opened it, which is what `catalogProblems`
    // says about a scene whose exit repeats its enter.
    {
      key: "attendance",
      enter: [{ do: "expand", target: "tt-review-attendance-bar" }],
      exit: [{ do: "collapse", target: "tt-review-attendance-bar" }],
      endsWithView: true,
    },
    // Every region of Zeiten prüfen is folded on a first visit — the page carries five of them — so
    // each chapter opens its own and folds it again. **One scene per region**, for the reason the
    // Konto and the Verstöße spell out: a scene is left the moment the next step names a different
    // one, so a second scene inside this one would fold the region between two steps that both need
    // it open. *Nur Lücken* is therefore a step action, and this exit undoes it as well — and it has
    // to, because that switch is not even rendered while the region is shut.
    // The matrix read as violations, and back to hours afterwards. One scene for both steps, so
    // going from "what broke a rule" to the cell that broke it is not a flick through two readings.
    // The other readings of the grid, one scene each, and back to hours after every one: a reader who
    // jumps to Status from the timeline must not find the grid still reading Saldo.
    {
      key: "mode-balance",
      enter: [{ do: "click", target: "tt-matrix-mode-balance" }],
      exit: [{ do: "click", target: "tt-matrix-mode-worked" }],
      endsWithView: true,
    },
    {
      key: "mode-status",
      enter: [{ do: "click", target: "tt-matrix-mode-status" }],
      exit: [{ do: "click", target: "tt-matrix-mode-worked" }],
      endsWithView: true,
    },
    {
      key: "matrix-violations",
      enter: [{ do: "click", target: "tt-matrix-mode-violations" }],
      exit: [{ do: "click", target: "tt-matrix-mode-worked" }],
      endsWithView: true,
    },
    // A cell opened. `escape` rather than a second click: the popover is a menu, and clicking its
    // activator again while it is open is the one gesture that does not reliably close it.
    {
      key: "matrix-cell",
      enter: [{ do: "click", target: "tt-matrix-cell-first" }],
      exit: [{ do: "escape" }],
      endsWithView: true,
    },
    // A day away opened: the popover leads with its absences. `escape` for the same reason as above.
    {
      key: "matrix-absence",
      enter: [{ do: "click", target: "tt-matrix-absence-first" }],
      exit: [{ do: "escape" }],
      endsWithView: true,
    },
    // The selection, worked over two steps: two single cells taken with Strg, then the rectangle
    // with Umschalt. The scene only holds it and drops it again — the presses are the steps' own, so
    // each gesture is played under the text that explains it. The exit presses Escape, which is what
    // drops a selection on this page — leaving one standing would carry it into the steps that
    // follow, where a bar full of verdict buttons over an unrelated cell is the last thing the reader
    // should see.
    {
      key: "picked",
      enter: [],
      exit: [{ do: "escape" }],
      endsWithView: true,
    },
    // Only the people with a gap, and everybody back afterwards.
    {
      key: "only-gaps",
      enter: [{ do: "check", target: "tt-matrix-only-gaps" }],
      exit: [{ do: "uncheck", target: "tt-matrix-only-gaps" }],
      endsWithView: true,
    },
    // The Arbeitszeitkonto is the row's popover now, not a folded bar at the foot of the page. **One
    // scene for the whole chapter**, for the same reason the Verstöße have one: a scene is left the
    // moment the next step names a different one, so a second scene inside this one would close the
    // popover and reopen it between two steps that both need it open. The months sit inside it
    // unfolded, so they are no longer a step action either — only the export menu is.
    {
      key: "account",
      enter: [{ do: "click", target: "tt-matrix-row-first" }],
      exit: [{ do: "escape" }],
    },
    // The export menu, for the last step of the chapter. Its own scene rather than an action on the
    // step: an action is what a scene's exit undoes, so a step carrying one without a scene leaves
    // the menu hanging open over whatever comes next — which is exactly what `catalogProblems`
    // refuses to let ship.
    {
      key: "account-export",
      enter: [{ do: "click", target: "tt-review-export" }],
      exit: [{ do: "escape" }],
      endsWithView: true,
    },
  ],
  steps: [
    // ── Meine Zeiten ─────────────────────────────────────────────────────────
    //
    // One step on the tab strip as a whole — this is the feature's navigation — and then each view's
    // own tab as the first step *of that view*, rather than all four tabs described up front: a list of
    // destinations read before any of them has been seen is a list nobody remembers. Every later view
    // therefore opens on its own tab, which is also the tab the pointer has just pressed to get there.
    //
    // A single tab is anchored here, which the catalog otherwise avoids — `show-arrows` slides tabs
    // with a transform, and a slid-away tab has a healthy box that is nowhere on screen. It is safe
    // because the frame is 1280px wide, where all four fit without arrows; and if they ever do not,
    // `isAnchorVisible` refuses the slid-away tab and the step is skipped.
    onMy("nav", "tt-nav", "mdi-tab", { placement: "bottom" }),
    onMy("tab-my", "tt-tab-TimeTrackingMy", "mdi-clock-outline", { placement: "bottom" }),

    onMy("clock", "tt-my-clock", "mdi-play-circle-outline", { placement: "bottom" }),
    onMy("clock-project", "tt-clock-project", "mdi-briefcase-outline", {
      placement: "bottom",
      scene: "clock-project",
      actions: [
        { do: "type", target: "tt-clock-project", text: "Halle" },
        { do: "choose", text: "Wartung Halle 2" },
        { do: "escape" },
      ],
    }),
    onMy("clock-in", "tt-clock-in", "mdi-play", { placement: "bottom" }),
    // The chip in the app bar, on every page while the clock runs, with its own stop button.
    onMy("clock-chip", "tt-clock-chip", "mdi-clock-outline", { placement: "bottom", scene: "clock-running" }),
    // The Pause stamp, in the same scene: it only exists while the clock runs. Shown rather than
    // pressed — ending the break is a second tap, and a step that leaves the frame on a break would
    // hand the next scene a panel in a state it does not describe.
    onMy("clock-break", "tt-break-start", "mdi-coffee-outline", { placement: "bottom", scene: "clock-running" }),

    // The form, in four beats. Opened, it is shown whole before anything is zoomed: somebody who has
    // just seen a window appear wants to see what is in it, not to be pulled straight onto its first
    // field. Then one step per *group* of fields rather than one per field — the camera goes in once
    // onto the date, the times and the project together, and holds there while the pointer fills them.
    onMy("entry-open", "form-dialog", "mdi-plus", { placement: "top", scene: "new-entry" }),
    onMy("entry-basics", ["tt-entry-date", "tt-entry-from", "tt-entry-to", "tt-entry-project"], "mdi-clock-start", {
      placement: "top",
      scene: "new-entry",
      actions: [
        { do: "type", target: "tt-entry-from", text: "06:30" },
        { do: "type", target: "tt-entry-to", text: "15:00" },
        { do: "type", target: "tt-entry-project", text: "Halle" },
        { do: "choose", text: "Wartung Halle 2" },
      ],
    }),
    // The break and the figures together: the click is the cause, the figures below are the effect.
    onMy("entry-break", ["tt-entry-breaks", "tt-entry-figures"], "mdi-coffee-outline", {
      placement: "top",
      scene: "new-entry",
      actions: [{ do: "click", target: "tt-break-preset" }],
    }),
    // The note, and the save button it sits above — pointed at and explained, never pressed.
    onMy("entry-note", ["tt-entry-note", "form-dialog-submit"], "mdi-note-text-outline", {
      placement: "top",
      scene: "new-entry",
      actions: [{ do: "type", target: "tt-entry-note", label: "help.tour.timetracking.typed.note" }],
    }),

    // The button and the entry that prints, with the pointer resting on the entry: the picture of the
    // page comes next, and it has to come after the reader has seen where it is printed from.
    onMy("export", ["tt-my-export", "tt-my-export-certificate"], "mdi-download-outline", {
      placement: "left",
      scene: "my-export",
      actions: [{ do: "hover", target: "tt-my-export-certificate" }],
    }),
    // What the Monatsnachweis in that menu looks like, as the walkthrough cannot print one: page 1 of a
    // sample month rendered once by the backend's own generator — see docs/view-help/timetracking.md.
    // In the menu's scene, so the menu stays open behind the page.
    onMy("export-certificate", null, "mdi-file-document-outline", {
      scene: "my-export",
      images: [{ src: "view-help/timetracking-monatsnachweis.webp", alt: "help.tour.timetracking.image.certificate" }],
    }),

    onMy("week", "tt-my-week", "mdi-calendar-week", { placement: "bottom" }),
    // No zoom: the point is that the whole page follows the week, which only shows with all of it in view.
    onMy("week-back", "tt-my-week", "mdi-chevron-left", { placement: "bottom", scene: "week-paging", zoom: false }),

    onMy("stat-actual", "tt-stat-actual", "mdi-timer-outline", { placement: "bottom" }),
    onMy("stat-target", "tt-stat-target", "mdi-target", { placement: "bottom" }),
    onMy("stat-balance", "tt-stat-balance", "mdi-scale-balance", { placement: "bottom" }),
    onMy("stat-days", "tt-stat-days", "mdi-calendar-check-outline", { placement: "bottom" }),

    // Only rendered for someone with a work-time model. Absent is the normal case, not a fault.
    onMy("account", "tt-my-account", "mdi-bank-outline", { placement: "bottom" }),

    onMy("entries", "tt-my-entries", "mdi-format-list-bulleted", { placement: "right" }),
    onMy("entry-row", "tt-my-entry-first", "mdi-text-box-outline", { placement: "bottom" }),

    onMy("chart", "tt-my-chart", "mdi-chart-bar", { placement: "left" }),
    // The dot on the day that was clicked, not on the list it filters — the reader has just watched the
    // click land there. No zoom, so the list to the left is in view as it narrows.
    onMy("chart-filter", "tt-chart-day-2", "mdi-filter-outline", {
      placement: "left",
      scene: "day-filter",
      zoom: false,
    }),
    onMy("projects", "tt-my-projects", "mdi-chart-donut", { placement: "left" }),
    onMy("project-filter", "tt-project-first", "mdi-filter-outline", {
      placement: "left",
      scene: "project-filter",
      zoom: false,
    }),

    // ── Prüfung ──────────────────────────────────────────────────────────────
    onReview("tab-review", "tt-tab-TimeTrackingReview", "mdi-check-decagram-outline", { placement: "bottom" }),
    // All four filters as one step: they are one question — whose days, when, in what state, on which
    // site — and four steps on four fields of one card was a tour of a form. The page opens with
    // everybody (its default for a bare visit), so the step first empties the picker and
    // picks two people, then the state people use to find what is waiting for them. No zoom: the point
    // is the table under the fields answering each pick. No site is picked — "Erfasst" on one site is
    // empty on some days of the week, and a demonstration ending on an empty table is the one thing
    // this step must not do.
    onReview(
      "review-filters",
      ["tt-review-employees", "tt-review-period", "tt-review-status", "tt-review-project"],
      "mdi-filter-variant",
      {
        placement: "bottom",
        scene: "review-filters",
        zoom: false,
        actions: [
          { do: "clear", target: "tt-review-employees" },
          { do: "click", target: "tt-review-employees" },
          { do: "choose", text: "Max Mustermann" },
          { do: "choose", text: "Erika Musterfrau" },
          { do: "escape" },
        ],
      }
    ),
    // The matrix, whole, before any of it is read closely — somebody who has just seen a grid appear
    // wants to see its shape, not to be pulled onto one of its cells.
    onReview("matrix", "tt-matrix", "mdi-view-grid-outline", { placement: "top" }),
    // No zoom: the switch is small and what it changes is every cell under it, which only shows with
    // both in view.
    onReview("matrix-mode", "tt-matrix-mode", "mdi-tune-variant", { placement: "bottom", zoom: false }),
    onReview("mode-balance", "tt-matrix", "mdi-scale-balance", {
      placement: "bottom",
      scene: "mode-balance",
      zoom: false,
    }),
    onReview("mode-status", "tt-matrix", "mdi-list-status", { placement: "bottom", scene: "mode-status", zoom: false }),
    // Below the grid, all three: above it the bubble sat on the very switch the step is about.
    onReview("matrix-violations", "tt-matrix", "mdi-gavel", {
      placement: "bottom",
      scene: "matrix-violations",
      zoom: false,
    }),
    // The popover that replaced a table row. Whole first, like the entry form: it is a window, and a
    // reader who has just watched one open wants to see what is in it.
    onReview("matrix-cell", "tt-matrix-day", "mdi-card-text-outline", { placement: "right", scene: "matrix-cell" }),
    // A day with an absence opens the same window, headed by the absence: which kind, how much of the
    // day, and the button that takes this one day out of it. The sample opens Erika's half day
    // (`HALF_DAY` in the demo), which also shows what a paid half day credits.
    onReview("matrix-absence", "tt-matrix-absence", "mdi-calendar-account-outline", {
      placement: "right",
      scene: "matrix-absence",
    }),
    // The day's own heading, which judges a whole column at once. Explained where it sits rather than
    // opened: the menu it drops is the same three verdicts the cell popover already showed.
    onReview("matrix-day-head", "tt-matrix-day-head", "mdi-calendar-check-outline", {
      placement: "bottom",
      scene: "day-head-hover",
      actions: [{ do: "hover", target: "tt-matrix-day-head" }],
    }),
    // An empty cell is an offer, not a hole. No scene: the click opens the day form, whose Zeit and
    // Abwesenheit tabs have steps of their own further down, and opening it twice would explain the
    // same window twice.
    onReview("matrix-create", "tt-matrix-create-first", "mdi-plus-box-outline", {
      placement: "top",
      permissions: EDIT_FOREIGN,
      scene: "create-hover",
      actions: [{ do: "hover", target: "tt-matrix-create-first" }],
    }),
    // The chapter that fills that gap, start to finish: the form it opens, whole first; another person
    // added, so it is the Sammelerfassung it is; then saved, with the camera on the day it filled. Peter
    // rather than Erika: he is not in the grid, so the second person's day cannot land on one of hers
    // and draw a day of two entries.
    onReview("review-crew-open", "form-dialog", "mdi-account-group", {
      placement: "top",
      scene: "crew",
      permissions: EDIT_FOREIGN,
    }),
    onReview("review-crew", "tt-crew-people", "mdi-account-multiple-plus-outline", {
      placement: "bottom",
      scene: "crew",
      permissions: EDIT_FOREIGN,
      actions: [{ do: "click", target: "tt-crew-people" }, { do: "choose", text: "Peter Beispiel" }, { do: "escape" }],
    }),
    // Saved, and the day it filled pointed at. The one save the walkthrough presses: the demo keeps it
    // (see the `crew` scene), so the reader sees the gap close rather than a save that failed.
    onReview("review-crew-save", "tt-matrix-created", "mdi-content-save-outline", {
      placement: "bottom",
      scene: "crew",
      permissions: EDIT_FOREIGN,
      actions: [{ do: "click", target: "form-dialog-submit" }],
    }),
    // Strg on the two top corners of a block of two people × three days (`markPickDemo` on the page
    // picks it: hours in every cell, entries still waiting). The camera goes in on the whole block, so
    // the day left out between the two is in shot, and so is the cell the next step Umschalt-clicks.
    onReview(
      "matrix-pick-ctrl",
      ["tt-matrix-pick-from", "tt-matrix-pick-to", "tt-matrix-pick-block"],
      "mdi-select-multiple",
      {
        placement: "bottom",
        scene: "picked",
        actions: [
          { do: "click", target: "tt-matrix-pick-from", with: "ctrl" },
          { do: "click", target: "tt-matrix-pick-to", with: "ctrl" },
        ],
      }
    ),
    // Umschalt on the block's bottom-left corner: the rectangle from the last Strg cell holds both
    // earlier ones, so the selection grows into the block instead of jumping somewhere else. Framed
    // with the bar under the table, which is what the block is for.
    onReview(
      "matrix-pick-shift",
      ["tt-matrix-pick-from", "tt-matrix-pick-to", "tt-matrix-pick-block", "tt-matrix-picked"],
      "mdi-select-group",
      {
        placement: "top",
        scene: "picked",
        actions: [{ do: "click", target: "tt-matrix-pick-block", with: "shift" }],
      }
    ),
    // Only the people with a gap in the period, worst first — the queue that used to be a page.
    onReview("only-gaps", "tt-matrix", "mdi-filter-outline", { placement: "top", scene: "only-gaps", zoom: false }),
    // Inside the matrix's horizontal scroller, which is the point: it exercises the sideways path.
    onReview("total", "tt-matrix-total", "mdi-sigma", { placement: "top" }),
    // The form's second tab, opened from the header's Abwesenheit button. The tabs first — they are
    // what is new about this window — then the fields filled in for an afternoon off, then the hint
    // under them, which is where the paid credit and the overlap rule are said.
    //
    // Urlaub, not a kind further down: `choose` presses only an option that is on screen, and the
    // list is taller than its menu, so Berufsschule sat below the fold and the step was dropped.
    onReview("review-absence-open", "tt-crew-mode", "mdi-calendar-account-outline", {
      placement: "bottom",
      scene: "absence",
      permissions: EDIT_FOREIGN,
    }),
    onReview("review-absence-kind", ["tt-absence-kind", "tt-absence-span"], "mdi-format-list-bulleted", {
      placement: "bottom",
      scene: "absence",
      permissions: EDIT_FOREIGN,
      actions: [
        { do: "click", target: "tt-absence-kind" },
        { do: "choose", text: "Urlaub" },
        { do: "click", target: "tt-absence-span-HALFDAY" },
      ],
    }),
    // Below the hint, over the dialog's buttons: above it, the bubble covered the kind and the span the
    // step before had just filled in.
    onReview("review-absence-rules", "tt-absence-hint", "mdi-scale-balance", {
      placement: "bottom",
      scene: "absence",
      permissions: EDIT_FOREIGN,
    }),
    // Explained, never pressed: approving a period is a write.
    onReview("review-approve", "tt-review-approve-period", "mdi-check-all", { placement: "bottom" }),
    onReview("review-export", "tt-review-export", "mdi-download-outline", { placement: "bottom" }),

    // *Tag für Tag*, between the filters and the table: the week as employees × days, and the switch
    // that turns it into the queue of people who are behind. No zoom on the switch's step — the point
    // is the grid beside it losing every complete row, which only shows with both in view.

    // The banner at the top of the page, closed, then unfolded by the scene so the names are on
    // screen for the step that follows — the one who never clocked out, which the board sorts to the
    // top because it is the entry it most needs to show. No zoom on either: the bar is what carries
    // the two figures, and the camera pulling in onto a row loses the bar that summarises it.
    onReview("attendance-bar", "tt-review-attendance", "mdi-account-clock-outline", {
      placement: "bottom",
      zoom: false,
    }),
    onReview("attendance-stale", "tt-attendance-stale", "mdi-alert-outline", {
      placement: "top",
      scene: "attendance",
      zoom: false,
    }),

    // ── Arbeitszeitkonto ─────────────────────────────────────────────────────
    //
    // The columns first, in the grid, so the reader sees that the account *is* two of the figures
    // they have been reading all along — it was a page, then a tab, then a folded bar, and the one
    // thing every one of those shapes got wrong was suggesting it lives somewhere else. Then the
    // row's popover, which is where the whole of it is.
    //
    // Two steps that once pointed at a filter row of their own and at a sentence inside a table are
    // gone: the page has one period now (see `review-filters`), and "Ohne Anfangssaldo" is a line in
    // the block the step after this one already points at.
    onReview("account-bar", "tt-matrix-account-cols", "mdi-scale-balance", { placement: "bottom", zoom: false }),
    onReview("account-table", "tt-matrix-account-cols", "mdi-table-column", { placement: "bottom" }),
    onReview("account-row", "tt-matrix-account", "mdi-scale-balance", { placement: "left", scene: "account" }),
    // The pointer rests on the first month's print symbol, so the picture after this step is of a page
    // the reader has just seen where to get.
    onReview("account-months", "tt-matrix-months", "mdi-calendar-month-outline", {
      placement: "left",
      scene: "account",
      actions: [{ do: "hover", target: "tt-matrix-month-print" }],
    }),
    // The page the symbol beside each month prints — the same sample page as on Meine Zeiten, in the
    // popover's scene, so the popover stays open behind it.
    onReview("account-certificate", null, "mdi-file-document-outline", {
      scene: "account",
      images: [{ src: "view-help/timetracking-monatsnachweis.webp", alt: "help.tour.timetracking.image.certificate" }],
    }),
    onReview("account-export", "tt-review-export", "mdi-download-outline", {
      placement: "left",
      scene: "account-export",
    }),

    // The closing "where to find this again" step, on the last view the walk reaches. It used to sit
    // on a Verstöße screen of its own; the findings are a reading of the matrix now, so it comes home
    // to the page the walk ends on.
    onReview("again", "view-help-trigger", "mdi-help-circle-outline", { placement: "bottom" }),

    // ── Zusammenhänge ──────────────────────────────────────────────────────────
    //
    // Not a tour of two more pages: these are other features' screens, and what matters here is only
    // where the figures of the six views come from. So every step names the setting *and* the place in
    // the six views it shows up — the list, three groups of one model's fields, and the assignment with
    // the account's seed. No step for the name, the colour or the planned times: nothing in the six
    // views is computed from them.
    onModels("models", "tt-model-list", "mdi-cog-outline", { placement: "bottom" }),
    // The model is opened by the scene and the camera goes straight onto its first group, which sits
    // at the top of the window: the window is seen opening on the way.
    onModels("model-limits", "tt-model-limits", "mdi-timer-alert-outline", {
      placement: "bottom",
      scene: "model-edit",
      permissions: EDIT_MODELS,
    }),
    onModels("model-days", "tt-model-days", "mdi-calendar-week", {
      placement: "top",
      scene: "model-edit",
      permissions: EDIT_MODELS,
    }),
    onModels("model-breaks", "tt-model-breaks", "mdi-coffee-outline", {
      placement: "top",
      scene: "model-edit",
      permissions: EDIT_MODELS,
    }),
    // The page's own tabs, framed, while the pointer clicks "Unternehmen" — the model is assigned there.
    onAssignment("employee-tabs", "employee-tabs", "mdi-tab", {
      placement: "bottom",
      scene: "employee-company",
      actions: [{ do: "openTab", target: "employee-tab-company" }],
    }),
    // Further down the tab: the camera pulls out, the page scrolls, and the camera goes in on it. On a
    // reload the page is opened on the tab itself.
    onAssignment("assignment", "tt-assignment", "mdi-account-clock-outline", {
      placement: "top",
      scene: "employee-company",
      at: { params: { employeeId: "demo-1" }, hash: "company" },
    }),

    // ── Wohin die Stunden gehen ──────────────────────────────────────────────
    //
    // The other direction, and the half this chapter was missing: the Zeiten register of the project
    // the hours were booked on — its totals, and their check against the project's reports — and the
    // planning that says what a person was supposed to be doing. The pointer presses the register's
    // tab with the whole page in view, as the projects tour does — the camera would otherwise fly onto
    // the tab and back out onto the register for one press.
    onProject("project-times", "project-timetracking", "mdi-clock-outline", {
      placement: "bottom",
      zoom: false,
      scene: "project-times",
      permissions: REVIEW_FOREIGN,
      actions: [{ do: "openTab", target: "project-tab-timetracking" }],
    }),
    // Further down the same register, so no tab to press: the scene is the one the step above opened.
    // The section also needs a reading right on both report types (`LewProjectTimeTrackingTab`), which
    // `permissions` cannot say — it is "any of", and this is "both". So the step carries the register's
    // gate, and a reader missing a report right loses it the way any step does whose region never
    // renders: it drops out of the timeline.
    onProject("project-reconciliation", "project-reconciliation", "mdi-file-compare", {
      placement: "top",
      scene: "project-times",
      permissions: REVIEW_FOREIGN,
    }),
    // The employee lane of the planning board: what is planned there decides which empty days the
    // Mitarbeiter page may call missing (`FrontendTimeCoverageService`, ABSENT over MISSING).
    // Placed below the lane, not above it: the text would otherwise sit over the very bars it is
    // about — the absence among them, in the colour of its kind. No zoom: the scene's `enter` presses
    // Heute first, and with the camera free it dived onto the button for the press and swung back out
    // onto the lane straight after — a zoom in and out for one click. The planning tour opens its
    // board with a `zoom: false` step for the same reason.
    onPlanning("planning-absence", "planning-section-employees", "mdi-calendar-account-outline", {
      placement: "bottom",
      scene: "planning-board",
      zoom: false,
    }),
  ],
}
